import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState, } from 'react';
import { View, Alert, Keyboard, TouchableWithoutFeedback, } from 'react-native';
import { useSharedValue } from 'react-native-reanimated';
import { IconHome, IconUnion, IconLocation, IconPostageStamp, } from 'design-system/icons';
import { IconRoad } from 'design-system/icons/IconRoad';
import { SettingsItemInput, SettingsSection } from 'design-system';
import { USStatePickerModal } from 'design-system/modals/USStatePickerModal';
import { useAppSelector } from 'store/hooks';
import { validateUSPostcode } from 'utils/validation';
import { useKeyboardAdjustPan } from 'hooks/useKeyboardAdjustPan';
import IconLocationTick from 'design-system/icons/IconLocationTick';
import { US_STATES } from 'features/borrow/monevo/models';
const strings = {
    defaultTitle: 'Choose..',
};
const AddressInput = React.forwardRef(({ defaultAddress, onSubmitEditing, noExteriorView }, ref) => {
    const statePickerSharedValue = useSharedValue(0);
    const userCountry = useAppSelector((state) => state.user.user.guessedHomeCountry);
    const [state, setState] = useState({
        buildingNumber: '',
        subBuildingName: '',
        postcode: '',
        streetName: '',
        townOrCity: '',
        stateCode: undefined,
    });
    useKeyboardAdjustPan();
    const townRef = useRef(null);
    const streetRef = useRef(null);
    const postCodeRef = useRef(null);
    const flatNumberRef = useRef(null);
    const buildingNumberRef = useRef(null);
    useEffect(() => {
        const address = defaultAddress;
        if (address) {
            const { buildingNumber, subBuildingName, streetName, townOrCity, postcode, stateCode, } = address;
            const stateObj = US_STATES.find((state) => state.value === stateCode);
            setState((oldState) => ({
                ...oldState,
                postcode: postcode || '',
                buildingNumber: buildingNumber || '',
                streetName: streetName || '',
                subBuildingName: subBuildingName || '',
                townOrCity: townOrCity || '',
                stateCode: stateObj,
            }));
        }
    }, [defaultAddress]);
    const onSetPostCode = useCallback((postcode) => {
        setState((oldState) => ({ ...oldState, postcode }));
    }, []);
    const onSetCity = useCallback((townOrCity) => {
        setState((oldState) => ({ ...oldState, townOrCity }));
    }, []);
    const onSetFlatNumber = useCallback((subBuildingName) => {
        setState((oldState) => ({ ...oldState, subBuildingName }));
    }, []);
    const onSetBuildingNumber = useCallback((buildingNumber) => {
        setState((oldState) => ({ ...oldState, buildingNumber }));
    }, []);
    const onSetStreetName = useCallback((streetName) => {
        setState((oldState) => ({ ...oldState, streetName }));
    }, []);
    const onSelectState = useCallback((stateObj) => {
        setState((oldState) => ({
            ...oldState,
            stateCode: stateObj,
        }));
    }, []);
    const handleSelectState = useCallback(() => {
        Keyboard.dismiss();
        statePickerSharedValue.value = 0;
        USStatePickerModal.show(statePickerSharedValue, onSelectState);
    }, [onSelectState, statePickerSharedValue]);
    const validateInputs = useCallback(async () => {
        Keyboard.dismiss();
        const { stateCode, postcode, streetName, townOrCity, buildingNumber, subBuildingName, } = state;
        if (!buildingNumber) {
            return Alert.alert('Hold on 💫', 'You need to enter a building number.', [{ onPress: () => buildingNumberRef.current?.focus(), text: 'OK' }]);
        }
        if (!streetName)
            return Alert.alert('Hold on 💫', 'You need to set your street name.', [
                { onPress: () => streetRef.current?.focus(), text: 'OK' },
            ]);
        if (!townOrCity)
            return Alert.alert('Hold on 💫', 'You need to set your town or city.', [
                { onPress: () => townRef.current?.focus(), text: 'OK' },
            ]);
        if (!stateCode) {
            return Alert.alert('Hold on 💫', 'You need to set your state.', [
                { onPress: handleSelectState, text: 'OK' },
            ]);
        }
        const postcodes = validateUSPostcode(postcode);
        if (!postcode || !postcodes)
            return Alert.alert('Hold on 💫', 'You need to enter a valid zip code.', [{ onPress: () => postCodeRef.current?.focus(), text: 'OK' }]);
        const body = {
            streetName,
            countryIsoCode: userCountry,
        };
        if (state.stateCode?.value)
            body.stateCode = state.stateCode.value;
        if (postcode)
            body.postcode = postcode;
        if (buildingNumber)
            body.buildingNumber = buildingNumber;
        if (subBuildingName)
            body.subBuildingName = subBuildingName;
        if (townOrCity)
            body.townOrCity = townOrCity;
        return body;
    }, [handleSelectState, state, userCountry]);
    useImperativeHandle(ref, () => ({ validateInputs }));
    const Wrapper = useMemo(() => {
        if (noExteriorView) {
            return View;
        }
        return SettingsSection;
    }, [noExteriorView]);
    return (<Wrapper noMarginTop>
        <SettingsItemInput maxLength={50} icon={<IconUnion />} ref={flatNumberRef} description="Apartment / House (optional)" value={state.subBuildingName} onChangeText={onSetFlatNumber} onClearTextInput={() => {
            onSetFlatNumber('');
        }} placeholder={strings.defaultTitle} blurOnSubmit={false} returnKeyType="next" onSubmitEditing={() => buildingNumberRef.current?.focus()}/>
        <SettingsItemInput maxLength={10} ref={buildingNumberRef} icon={<IconHome gradientKey="apricot"/>} description="Building number" value={state.buildingNumber} onChangeText={onSetBuildingNumber} onClearTextInput={() => {
            onSetBuildingNumber('');
        }} placeholder={strings.defaultTitle} blurOnSubmit={false} returnKeyType="next" onSubmitEditing={() => streetRef.current?.focus()}/>
        <SettingsItemInput maxLength={50} ref={streetRef} icon={<IconRoad />} description="Street name" value={state.streetName} onChangeText={onSetStreetName} onClearTextInput={() => {
            onSetStreetName('');
        }} placeholder={strings.defaultTitle} blurOnSubmit={false} returnKeyType="next" onSubmitEditing={() => townRef.current?.focus()}/>
        <SettingsItemInput maxLength={50} ref={townRef} icon={<IconLocation gradientKey="lightPink"/>} description="Town / City" value={state.townOrCity} onChangeText={onSetCity} onClearTextInput={() => {
            onSetCity('');
        }} placeholder={strings.defaultTitle} blurOnSubmit={false} returnKeyType="next" onSubmitEditing={handleSelectState}/>
        <TouchableWithoutFeedback onPress={handleSelectState}>
          <View pointerEvents="box-only">
            <SettingsItemInput icon={<IconLocationTick />} description="State" editable={false} value={state.stateCode?.label || ''} placeholder={strings.defaultTitle}/>
          </View>
        </TouchableWithoutFeedback>
        <SettingsItemInput maxLength={5} ref={postCodeRef} returnKeyType="done" description="Zip code" value={state.postcode} keyboardType="number-pad" icon={<IconPostageStamp />} onChangeText={onSetPostCode} onClearTextInput={() => {
            onSetPostCode('');
        }} placeholder={strings.defaultTitle} onSubmitEditing={onSubmitEditing}/>
      </Wrapper>);
});
export default AddressInput;
